import {
  SLS_about,
  SLS_status,
  SLS_watertest,
  SLS_options_map,
} from 'lib/sls';
import {getErrorCode, ScanningErrors} from './errorCodes';
import {events} from 'lib';
import {chemicalBaseTypes} from 'ui/helpers/constants';
import slsProgressTracker from 'services/SLSProgressTracker';
import {compare_versions} from 'util/version';
import {
CURRENT_SMARTLABSERVICE_VERSION,
} from 'ui/helpers/constants';
import {XFCInit, XFCScan} from 'lib/xfConnect';
import xfcProgressTracker from './XFCProgressTracker';

const LSKEY_HISTORY = 'SCAN_HISTORY';
// If SCAN_ERROR_KEY is modified, modify it in the constants file too
const SCAN_ERROR_KEY = 'SCAN_ERROR_KEY';
const ScannerType = {
  NONE: undefined,
  SMARTLAB: 'SmartLab',
  XPRESS_FLEX: 'Xpress Flex',
};

class TestConnect {
  _scanResults = undefined;
  _currentScanner = ScannerType.NONE;
  _chemicalBaseTypeID = undefined;
  _url = undefined;
  _statusPromise = null;
  _currentDisk = undefined;

  reset() {
    this._scanResults = undefined;
    this._currentScanner = ScannerType.NONE;
    this._chemicalBaseTypeID = undefined;
    this._url = undefined;
    this._currentDisk = undefined;
    // events.publish('scanner.cancel');
  }

  /**
   * Removes the current scanner by setting the property to NONE
   * @returns {void}
   */
  removeCurrentScanner() {
    this._currentScanner = ScannerType.NONE;
  }

  get url() {
    return this._url;
  }

  get results() {
    return this._scanResults;
  }

  get isScanning() {
    return this._currentScanner !== ScannerType.None;
  }

  get currentScanner() {
    return this._currentScanner;
  }

  get isSmartLabCurrentScanner() {
    return this._currentScanner === ScannerType.SMARTLAB;
  }

  get isXFCurrentScanner() {
    return this._currentScanner === ScannerType.XPRESS_FLEX;
  }

  get currentDisk() {
    return this._currentDisk;
  }

  // Retrieves currently connected scanners and service versions. Returns in a promise.
  getStatus(force = false) {
    if (!force && this._statusPromise !== null) {return this._statusPromise;}
    this._statusPromise = Promise.all([

        // First promise: get SLS scanner name.
      SLS_status()
        .catch(err => {
          console.error(err);
          return null;
        }),

        // Second promise: get SLS version.
      SLS_about()
        .catch(err => {
          console.error(err);
          return null;
        }),

      //End Promise.all
    ]).then(res_arr => {
        // Pack results of all calls into one return object.
      let [sls_status, sls_about] = res_arr;
      let ret = {
        scanners: [],
        sls_version: null,
        sls_version_outofdate: null,
        sls_client_version: null,
        sls_can_update: null,
        sls_must_update: null,
        sls_current_firmware: null,
        sls_available_firmware: null,
      };

        // Retrieve information from SLS_status call.
      if (sls_status !== null) {
        if(sls_status.Name !== undefined && sls_status.Name !== '') {
          ret.scanners.push(sls_status.Name);
        }

        ret.sls_can_update = sls_status.CanDoUpdate;
          // Status of 4 means an update is required to use the scanner (https://updates.waterlinkconnect.com/docs/API_Documentation_1_4.pdf).
        ret.sls_must_update = sls_status.Status === 4;
        ret.sls_current_firmware = sls_status.CurentFirmware;
        ret.sls_available_firmware = sls_status.AvailableFirmware;
      }

        // Retrieve information from SLS_about call
      if(sls_about !== null) {
        ret.sls_version = sls_about.ServiceVersion;
        ret.sls_version_outofdate = compare_versions(ret.sls_version, CURRENT_SMARTLABSERVICE_VERSION) < 0;
        sls_about.Apps.forEach((app) => {
          if(app.Name === 'Smart Lab Client') {
            ret.sls_client_version = app.Version;
          }
        });
      }

      ret.scanners.push('Manual');
      return ret;
    });
    return this._statusPromise;
  }

  normalizeChemicalBaseTypeForScanners(chemicalBaseTypeID) {
    switch (chemicalBaseTypeID) {
      case chemicalBaseTypes.saltWaterSpa: return chemicalBaseTypes.saltWaterPool;
      case chemicalBaseTypes.synergyPool: return chemicalBaseTypes.chlorinePool;
      case chemicalBaseTypes.synergySpa: return chemicalBaseTypes.chlorineSpa;
      default: return chemicalBaseTypeID;
    }
  }

  // Converts a chemicalBaseTypeID to a string indicating the type of sample.
  chemicalBaseTypeIDToSampleName(chemicalBaseTypeID) {
    switch (chemicalBaseTypeID) {
      case chemicalBaseTypes.brominePool:
      case chemicalBaseTypes.bromineSpa:
        return 'Bromine';
      case chemicalBaseTypes.softswimPool:
      case chemicalBaseTypes.softSoakSpa:
        return 'Biguanide';
      case chemicalBaseTypes.mineralSpringsPool:
      case chemicalBaseTypes.saltWaterPool:
      case chemicalBaseTypes.saltWaterSpa:
        return 'Salt';
      case chemicalBaseTypes.chlorinePool:
      case chemicalBaseTypes.chlorineSpa:
      case chemicalBaseTypes.synergyPool:
      case chemicalBaseTypes.synergySpa:
      default:
        return 'Chlorine';
    }
  }

  _retrieveHistory() {
    let history = localStorage.getItem(LSKEY_HISTORY);

    return history === null ? [] : JSON.parse(history);
  }

  _saveHistory(history) {
    localStorage.setItem(LSKEY_HISTORY, JSON.stringify(history));
  }

  storeHistory(item) {
    let history = this._retrieveHistory();

    history.push(item);
    if(history.length > 6) {
      history.shift();
    }

    this._saveHistory(history);
  }

  updateHistory(updated_item) {
    let history = this._retrieveHistory();

    history.forEach(item => {
      if(item.id === updated_item.id) {
        item.data = updated_item.data;
      }
    });

    this._saveHistory(history);
  }

  getHistory() {
    let history = this._retrieveHistory();

    return history;
  }

  _processScanningError(err) {
    console.error(err);
    if(err.code !== undefined) {

      let message = err.message ? err.message : getErrorCode(err.code, this._currentScanner);

      // Publish event to display error banner.
      events.publish('error.user',{
        message: message,
        key: SCAN_ERROR_KEY,
      });

      // Currently only storing error in history if its from Accuscan (message will be a string).
      if(typeof message !== 'object') {
        this.storeHistory({
          id: 'err' + new Date().getTime().toString(),
          scanner: this._currentScanner,
          time: new Date(),
          data: {
            errorMessage: message,
            key: SCAN_ERROR_KEY,
          },
        });
      }
    }

    this.reset();
    events.publish('scanner.cancel');
    return {};
  }

  /**
   * Initializes the AccuFlex™ device and begins the scanning process
   * @param {Boolean} isBromineScan Determines if the water test involves a bromine system or not
   * @returns {void}
   */
  startXFScan(isBromineScan) {
    // Publish events to trigger progress bar.
    this._currentScanner = ScannerType.XPRESS_FLEX;
    this._currentDisk = undefined;
    this._url = window.location.hash;

    events.publish('cancel.error', {key: SCAN_ERROR_KEY});
    events.publish('scanner.start');

    // First initialize the XF scanner to prep for scanning
    XFCInit()
      .then(res => {
        if (!res.deviceConnected) {
          throw {code: ScanningErrors.XFC_SERVICEOFFLINE};
        }
      })
      .then(() => {
      // When scan begins, promise is resolved immediately so we can begin progress tracking
        XFCScan(isBromineScan)
          .then(() => xfcProgressTracker.start());
      })
    // If init or scan fails, it is likely a service offline error
    .catch(err => this._processScanningError(err));
  }

  startSmartLab(chemicalBaseTypeID, disk) {
    // Publish events to trigger progress bar.
    this._currentScanner = ScannerType.SMARTLAB;
    this._currentDisk = disk;
    this._url = window.location.hash;
    slsProgressTracker.start();

    events.publish('cancel.error', {key: SCAN_ERROR_KEY});
    events.publish('scanner.start');

    // Get the name of the sample from the chemicalBaseTypeID
    let sampleName = this.chemicalBaseTypeIDToSampleName(chemicalBaseTypeID);

    // First we retrieve mappings generated by SLS 'options' API call.
    return SLS_options_map()

      // Use the mappings to retrieve the sample and reagent ids.
      .then(res => {

        // Ensure disk is available on this scanner.
        if(res.reagent_map[disk] === undefined) {throw {code: ScanningErrors.TCSMARTLAB_REAGENTNOTAVAILABLE};}

        if (disk === 'Meter Check Disk') {
          // For a meter check disk, we take the first valid sample type (ie chemicalBaseTypeID is not used).

          // Ensure there is at least one valid sample.
          if(Object.keys(res.sample_map).length === 0) {throw {code: ScanningErrors.TCSMARTLAB_SAMPLENOTAVAILABLE};}

          return {
            sample: res.sample_map[Object.keys(res.sample_map)[0]],
            reagent: res.reagent_map[disk],
          };
        }else{
          // Ensure sample is available on this scanner.
          if(res.sample_map[sampleName] === undefined) {throw {code: ScanningErrors.TCSMARTLAB_SAMPLENOTAVAILABLE};}
          return {
            sample: res.sample_map[sampleName],
            reagent: res.reagent_map[disk],
          };
        }

      })
      .catch((err) => {
        /* Could be an error calling SLS_options (ie no connectivity),
        a json decoding error, or an unpacking error. */
        if (err.code !== undefined) {throw err;}
        throw {code: ScanningErrors.TCSMARTLAB_OPTIONS, error: err};
      })

      // Start a test via the Smart Lab Service 'watertest' API call.
      .then(ret => SLS_watertest(ret.sample, ret.reagent))
      .then(response => {

        // Detect if any errors occured while scanning and report them.
        if(response.Errors.length > 0) {

          let errcode = response.Errors[0].Source * 1000 + response.Errors[0].Code;

          // Default blank error code, show banner but do not fail.
          if (response.Errors[0].Code == 13) {

            // Publish event to display error banner.
            events.publish('error.user',{
              message: getErrorCode(errcode, this._currentScanner),
              key: SCAN_ERROR_KEY,
            });

          }else{
            throw {code: errcode};
          }
        }

        // Pack results into a dictionary that maps result names to their values.
        // eg. map["Free Cl"] = 3.1
        this._scanResults = response.Results.reduce((map, result) =>{
          map[result.Name] = result.Value;
          return map;
        }, {});

        this._currentScanner = ScannerType.NONE;
        events.publish('scanner.complete', this._scanResults);
        return this._scanResults;
      })
      .catch((err) => {
        /* Could be an error calling SLS_watertest (ie no connectivity)
        or a json decoding error. */
        if (err.code !== undefined) {throw err;}
        throw {code: ScanningErrors.TCSMARTLAB_WATERTEST, error: err};
      })
      .catch(err => this._processScanningError(err))
      .finally(() => {
        slsProgressTracker.stop();
        this._currentDisk = undefined;
      });
  }

  startSmartLabCalibration() {
    // At the moment, calibration can only be run through SmartLab (nontouch version).
    return this.startSmartLab(null, 'Meter Check Disk');
  }
}
let testConnect = new TestConnect();

export default testConnect;